import React, {useMemo, useRef, useState} from 'react';
import {Carousel} from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import {Recco} from '../../../models';
import RoundedProfilePicture from '../../../components/profile-picture/rounded-profile-picture.comonent';

interface PublicReccoCardProps {
  isFeatured?: boolean;
  recco: Recco;
}

export const PublicReccoCard: React.FC<PublicReccoCardProps> = ({recco, isFeatured = false}) => {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [expanded, setExpanded] = useState(false);

  const message = useMemo(() => recco.message?.trim() || 'Recommendation', [recco.message]);
  const mediaResources = recco.resources?.filter(resource => !!resource.url) ?? [];
  const hasMedia = mediaResources.length > 0;

  const containerClasses = isFeatured ? 'bg-gradient-to-br from-[#3C7CFF] to-[#60A6FF] text-white' : 'bg-white text-gray-900';

  const messageClasses = `${isFeatured ? 'text-white/95' : 'text-gray-800'} ${expanded ? '' : 'line-clamp-3'} text-base leading-relaxed`;

  return (
    <article className={`rounded-3xl p-6 ${containerClasses}`}>
      {hasMedia && (
        <div className={`-mx-6 mb-5 overflow-hidden rounded-2xl ${isFeatured ? 'bg-white/10' : 'bg-gray-100'}`}>
          <Carousel
            emulateTouch
            key={recco.id}
            showIndicators={mediaResources.length > 1}
            showStatus={false}
            showThumbs={false}
            swipeable
            useKeyboardArrows
            onChange={index => {
              videoRefs.current.forEach((video, i) => {
                if (video) {
                  if (i === index) {
                    video.play().catch(() => {});
                  } else {
                    video.pause();
                  }
                }
              });
            }}>
            {mediaResources.map((item, index) => (
              <div key={item.id || item.url || index} className="relative w-full bg-black pb-[100%]">
                {item.type === 'video' ? (
                  <video
                    className="absolute left-0 top-0 h-full w-full object-contain"
                    ref={el => (videoRefs.current[index] = el)}
                    src={item.url}
                    autoPlay
                    controls
                    loop
                    muted
                    playsInline
                  />
                ) : (
                  <img alt="Recco media" className="absolute left-0 top-0 h-full w-full object-contain" src={item.url} />
                )}
              </div>
            ))}
          </Carousel>
        </div>
      )}

      <div className="space-y-4">
        <p className={messageClasses}>{message}</p>
        {!expanded && message.length > 160 && (
          <button className={`text-sm font-semibold ${isFeatured ? 'text-white' : 'text-blue-600'}`} onClick={() => setExpanded(true)}>
            Read more
          </button>
        )}
      </div>

      <div className={`mt-6 flex items-center gap-3 rounded-2xl ${isFeatured ? 'bg-white/15' : 'bg-gray-50'} px-4 py-3`}>
        <RoundedProfilePicture name={recco.createdBy?.displayName} size={40} url={recco.createdBy?.profilePictureResource?.url} />
        <div className="flex flex-col">
          <span className={`text-sm font-semibold ${isFeatured ? 'text-white' : 'text-gray-900'}`}>
            {recco.createdBy?.displayName || 'Recco Giver'}
          </span>
          <span className={`text-xs ${isFeatured ? 'text-white/80' : 'text-gray-500'}`}>
            {recco.createdBy?.bio || recco.createdBy?.title || ''}
          </span>
        </div>
      </div>

      {recco.cred?.skills && recco.cred.skills.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {recco.cred.skills.slice(0, 4).map(skill => (
            <span
              key={skill.id}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                isFeatured ? 'bg-white/20 text-white' : 'bg-[#f2e3ff] text-[#7c3aed]'
              }`}>
              {skill.skill?.name}
            </span>
          ))}
        </div>
      )}
    </article>
  );
};
